import React, { useEffect, useState, useRef } from 'react';
import { CiUser } from 'react-icons/ci';
import { LiaUserLockSolid } from 'react-icons/lia';
import { WiTime2 } from 'react-icons/wi';
import { TbNotes } from 'react-icons/tb';
import { GoHash } from 'react-icons/go';
import { FaAt } from 'react-icons/fa';
import { IoMdCloseCircleOutline } from 'react-icons/io';
import { IoAddOutline, IoReloadOutline } from 'react-icons/io5';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import Button from '../components/ui/Button/Button';
import { PiNotePencilBold } from 'react-icons/pi';
import { MdOutlinePeople, MdLockOutline } from 'react-icons/md';
import TableSearchFilter from '../components/utils/TableSearchFilter';
import ResponsiveTable from '../components/Table/ResponsiveTable';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchUsersWithAccess } from '../redux/actions/employeeActions';
import Loading from '../components/utils/Loading';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { EmployeeData } from '../redux/types/employeeTypes';

interface User {
  id: number;
  username: string;
  loginId: string;
  userType: string;
  createdOn: string;
  status: string;
}

// Format employee status to a more readable format
const formatEmployeeStatus = (status: string): string => {
  if (!status) return 'Unknown';

  // Convert underscore format to title case
  return status
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Convert employee data to user interface format
const mapEmployeeToUser = (employee: EmployeeData): User => {
  // Format the created date if available
  const formattedDate = employee.createdAt
    ? new Date(employee.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : 'N/A';

  return {
    id: employee.employeeId,
    username: employee.fullname,
    loginId: employee.email,
    userType: employee.designation?.name || 'Unknown',
    createdOn: formattedDate,
    status: formatEmployeeStatus(employee.employeeStatus),
  };
};

// Sample user data for fallback
const sampleUsers: User[] = [
  {
    id: 1,
    username: 'Ankit Singh',
    loginId: 'admin@manakengineering.com',
    userType: 'Project Lead',
    createdOn: '09-02-2024 07:49:59',
    status: 'Active',
  },
  // ... other sample users
];

const Users: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Get staff data from Redux
  const { usersWithAccess, loadingUsersWithAccess, error } = useAppSelector(
    state => state.employees
  );

  // Get user data from auth state
  const { user } = useAppSelector(state => state.auth);

  // State for permissions management
  const [userPermissions, setUserPermissions] = useState({
    employeeRead: false,
    employeeWrite: false,
    employeeEdit: false,
    canManageEmployee: false,
  });
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  // Add refreshing state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Local state
  const [searchValue, setSearchValue] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);

  // Use a ref to track if we've already shown the permission error
  const permissionErrorShown = useRef(false);

  // Initialize permissions from user data
  useEffect(() => {
    if (user) {
      setPermissionsLoading(true);
      // Super Admin has all permissions
      if (user.designation === 'superAdmin') {
        setUserPermissions({
          employeeRead: true,
          employeeWrite: true,
          employeeEdit: true,
          canManageEmployee: true,
        });
        setPermissionsLoading(false);
        return;
      }

      // For non-superAdmin users, check permissions
      if (user.permissions) {
        let employeeReadValue = false;
        let employeeWriteValue = false;
        let employeeEditValue = false;
        let canManageEmployeeValue = false;

        // Check if permissions is an object with numeric keys (like {0: {...}})
        if (typeof user.permissions === 'object' && '0' in user.permissions) {
          //@ts-ignore
          employeeReadValue = Boolean(user.permissions[0].employeeRead);
          //@ts-ignore
          employeeWriteValue = Boolean(user.permissions[0].employeeWrite);
          //@ts-ignore
          employeeEditValue = Boolean(user.permissions[0].employeeEdit);
          //@ts-ignore
          canManageEmployeeValue = Boolean(user.permissions[0].canManageEmployee);
        }
        // Check if permissions is an array
        else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
          employeeReadValue = Boolean(user.permissions[0].employeeRead);
          employeeWriteValue = Boolean(user.permissions[0].employeeWrite);
          employeeEditValue = Boolean(user.permissions[0].employeeEdit);
          canManageEmployeeValue = Boolean(user.permissions[0].canManageEmployee);
        }
        // Direct object access if neither of the above
        else if (typeof user.permissions === 'object') {
          employeeReadValue = Boolean(user.permissions.employeeRead);
          employeeWriteValue = Boolean(user.permissions.employeeWrite);
          employeeEditValue = Boolean(user.permissions.employeeEdit);
          canManageEmployeeValue = Boolean(user.permissions.canManageEmployee);
        }

        // Set the state with the determined values
        setUserPermissions({
          employeeRead: employeeReadValue,
          employeeWrite: employeeWriteValue,
          employeeEdit: employeeEditValue,
          canManageEmployee: canManageEmployeeValue,
        });
      }
      setPermissionsLoading(false);
    }
  }, [user]);

  // Separate useEffect for authentication check
  useEffect(() => {
    // Redirect if not authenticated
    if (user === null) {
      toast.error('Please log in to access this page', {
        duration: 3000,
        id: 'login-required', // Prevent duplicate toasts
      });
      navigate('/auth/login');
    }
  }, [user, navigate]);

  // Fetch users with access on component mount if user has permissions
  useEffect(() => {
    // Only run this effect if user data is available and permissions are loaded
    if (!user || permissionsLoading) return;

    const hasAccess = userPermissions.employeeRead || user?.designation === 'superAdmin';

    if (hasAccess) {
      dispatch(fetchUsersWithAccess())
        .unwrap()
        .catch(error => {
          toast.error(`Error fetching users data: ${error}`);
        });
    } else if (!permissionErrorShown.current) {
      // Only show the permission error once
      permissionErrorShown.current = true;
      toast.error("You don't have permission to view users", {
        duration: 3000,
        id: 'permission-error', // Prevent duplicate toasts
      });
    }
  }, [dispatch, userPermissions.employeeRead, user, permissionsLoading]);

  // Map employees to users when data is loaded
  useEffect(() => {
    if (usersWithAccess && usersWithAccess.length > 0) {
      const mappedUsers = usersWithAccess.map(employee => mapEmployeeToUser(employee));
      setUsers(mappedUsers);
      setFilteredUsers(mappedUsers);
    } else if (!loadingUsersWithAccess && usersWithAccess?.length === 0) {
      // Don't use sample data, set empty arrays instead
      setUsers([]);
      setFilteredUsers([]);
    }
  }, [usersWithAccess, loadingUsersWithAccess]);

  // Handle search functionality
  useEffect(() => {
    if (searchValue.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(
        user =>
          user.username.toLowerCase().includes(searchValue.toLowerCase()) ||
          user.loginId.toLowerCase().includes(searchValue.toLowerCase()) ||
          user.userType.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchValue, users]);

  // Handle edit user
  const handleEditUser = (userRow: User) => {
    // Check if user has permission to edit employees (user is the auth user, not the row)
    if (!userPermissions.employeeEdit && user?.designation !== 'superAdmin') {
      toast.error("You don't have permission to edit users", {
        duration: 3000,
      });
      return;
    }
    navigate(`/edit-user/${userRow.id}`);
  };

  // Handle navigation to add user page
  const handleAddUser = () => {
    // Check if user has permission to add employees
    if (!userPermissions.employeeWrite && user?.designation !== 'superAdmin') {
      toast.error("You don't have permission to add users", {
        duration: 3000,
      });
      return;
    }
    navigate('/add-employee');
  };

  // Add refresh function
  const handleRefresh = () => {
    const hasAccess = userPermissions.employeeRead || user?.designation === 'superAdmin';

    if (hasAccess) {
      setIsRefreshing(true);
      toast.loading('Refreshing users data...', { id: 'refresh-users' });

      dispatch(fetchUsersWithAccess())
        .unwrap()
        .then(() => {
          toast.success('Users data refreshed successfully', { id: 'refresh-users' });
        })
        .catch(error => {
          toast.error(`Error refreshing users data: ${error}`, { id: 'refresh-users' });
        })
        .finally(() => {
          setIsRefreshing(false);
        });
    } else {
      toast.error("You don't have permission to refresh users data", {
        duration: 3000,
      });
    }
  };

  const columns = [
    {
      key: 'id',
      header: 'ID',
      icon: <GoHash className="w-3 h-3" />,
      priority: 3,
    },
    {
      key: 'username',
      header: 'Username',
      icon: <CiUser className="w-3 h-3" />,
      priority: 5,
      maxWidth: '150px',
    },
    {
      key: 'loginId',
      header: 'Login-Id',
      icon: <FaAt className="w-3 h-3" />,
      priority: 5,
      minWidth: '180px',
    },
    {
      key: 'userType',
      header: 'User Type',
      icon: <LiaUserLockSolid className="w-3 h-3" />,
      priority: 3,
    },
    {
      key: 'status',
      header: 'Status',
      icon: <TbNotes className="w-3 h-3" />,
      priority: 5,
    },
    {
      key: 'action',
      header: 'Action',
      icon: <TbNotes className="w-3 h-3" />,
      priority: 5,
      renderCell: (_: any, row: User) => (
        <button
          className="px-2 py-0.5 text-xs bg-white text-[#28A745] rounded-full border border-[#28A745] inline-flex items-center gap-1"
          onClick={e => {
            e.stopPropagation();
            handleEditUser(row);
          }}
        >
          <PiNotePencilBold className="w-2.5 h-2.5" />
          Edit
        </button>
      ),
    },
  ];

  // Check if user has permission to view users
  const hasReadAccess = userPermissions.employeeRead || user?.designation === 'superAdmin';
  const hasWriteAccess = userPermissions.employeeWrite || user?.designation === 'superAdmin';
  const hasEditAccess = userPermissions.employeeEdit || user?.designation === 'superAdmin';

  if (permissionsLoading) {
    return (
      <ComponentWrapper icon={<MdOutlinePeople size={30} />} title="Users Management">
        <div className="flex justify-center items-center h-64">
          <Loading height="200px" />
        </div>
      </ComponentWrapper>
    );
  }

  // If user has no permission at all, show access denied message
  if (!hasReadAccess && user !== null) {
    return (
      <ComponentWrapper
        icon={<MdLockOutline size={24} />}
        title="Access Denied"
        className="min-h-[70vh] flex items-center justify-center"
      >
        <div className="text-center py-12 max-w-lg mx-auto">
          <div className="bg-red-100 text-red-600 p-4 rounded-full inline-flex mb-6">
            <MdLockOutline size={48} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Permission Required</h2>
          <p className="text-gray-600 mb-6">
            You don't have permission to access the Users Management page. Please contact your
            administrator if you need access to this feature.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </ComponentWrapper>
    );
  }

  return (
    <ComponentWrapper
      icon={<MdOutlinePeople size={30} />}
      title="Users Management"
      buttonsTwo={[
        {
          component: (
            <Button
              variant="secondary"
              size="sm"
              className="py-2 mr-2"
              icon={<IoReloadOutline />}
              onClick={handleRefresh}
              disabled={isRefreshing || loadingUsersWithAccess}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          ),
        },
        {
          component: hasWriteAccess ? (
            <Button
              variant="primary"
              size="sm"
              className="py-2"
              icon={<IoAddOutline />}
              onClick={handleAddUser}
            >
              Add User
            </Button>
          ) : null,
        },
      ]}
    >
      <TableSearchFilter
        searchValue={searchValue}
        searchPlaceholder="Search users..."
        onSearchChange={setSearchValue}
        showFilterButton={false}
      />

      <div className="bg-transparent md:bg-white rounded-lg p-2">
        {loadingUsersWithAccess ? (
          <div className="flex justify-center items-center ">
            <Loading height="400px" />
          </div>
        ) : error ? (
          <div className="text-center py-6">
            <p className="text-red-500">Error loading user data. Please try again.</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="max-w-md mx-auto">
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100">
                  <MdOutlinePeople size={32} className="text-gray-400" />
                </div>
              </div>
              <h3 className="text-xl font-medium text-gray-700 mb-2">No Users Available</h3>
              <p className="text-gray-500 mb-8">
                There are currently no users in the system. Add a new user to get started.
              </p>
              {hasWriteAccess && (
                <div className="flex justify-center">
                  <Button
                    variant="primary"
                    size="md"
                    className="px-6"
                    icon={<IoAddOutline className="text-lg" />}
                    onClick={handleAddUser}
                  >
                    Add User
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <ResponsiveTable
            data={filteredUsers}
            columns={hasEditAccess ? columns : columns.filter(col => col.key !== 'action')}
            statusColors={{
              Active: 'bg-green-100 text-green-800',
              Inactive: 'bg-red-100 text-red-800',
              Probation: 'bg-yellow-100 text-yellow-800',
              Terminated: 'bg-gray-100 text-gray-800',
              Resigned: 'bg-orange-100 text-orange-800',
              Retired: 'bg-blue-100 text-blue-800',
              'On Leave': 'bg-purple-100 text-purple-800',
            }}
            statusIcons={{
              Active: <IoMdCloseCircleOutline className="w-4 h-4 text-green-600" />,
              Inactive: <IoMdCloseCircleOutline className="w-4 h-4 text-red-600" />,
              Probation: <IoMdCloseCircleOutline className="w-4 h-4 text-yellow-600" />,
              Terminated: <IoMdCloseCircleOutline className="w-4 h-4 text-gray-600" />,
              Resigned: <IoMdCloseCircleOutline className="w-4 h-4 text-orange-600" />,
              Retired: <IoMdCloseCircleOutline className="w-4 h-4 text-blue-600" />,
              'On Leave': <IoMdCloseCircleOutline className="w-4 h-4 text-purple-600" />,
            }}
            itemsPerPageOptions={[10, 15, 20, 50]}
            defaultItemsPerPage={10}
            totalItems={filteredUsers.length}
            currentPage={1}
          />
        )}
      </div>
    </ComponentWrapper>
  );
};

export default Users;
